import React, { useEffect } from "react";
import { Suspense } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { useParams, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { renderPreview } from "../../../SectionElements/plugins/utils";
import RenderHtml from "../../../../common/AppSpecificConpnents/PartsTable/RenderHtml";
import { pingTask } from "../../../../util/apiService";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { SharedHistoryContext } from "../../../../common/RichTextEditor/LexicalEditor/context/SharedHistoryContext";
import { TableContext } from "../../../../common/RichTextEditor/LexicalEditor/plugins/TablePlugin";
import { ToolbarContext } from "../../../../common/RichTextEditor/LexicalEditor/context/ToolbarContext";
import Editor from "../../../../common/RichTextEditor/Editor/Editor";
import { buildImportMap } from "../../../../util/lexical";
import PlaygroundNodes from "../../../../common/RichTextEditor/LexicalEditor/nodes/PlaygroundNodes";
import PlaygroundEditorTheme from "../../../../common/RichTextEditor/LexicalEditor/themes/PlaygroundEditorTheme";
import {
  NODE_SELECTION_BLUE,
  isDescendantLockedPreview,
  isAuditPreviewFocusedLeafCheckbox,
  getDescendantLockedCheckboxStyle,
  getHaloWrapperStyle,
  getAuditPreviewReadonlyHaloCheckboxStyle,
  auditPreviewSectionDescendantWrapStyle,
} from "./nodeSelectionCheckboxStyles";

const AttachmentNode = ({ node, nodeNumber, parentContext, nodeType, onNavigate, checkboxState, disabled = false, showCheckbox = true, showHeading = null, checkboxLabelColor = null, isRevisionLocked = false }) => {
  const authUser = useSelector((state) => state.auth.user);
  const nodeHeading = `Attachment ${node.is_deleted ? "(Deleted)" : nodeNumber}`;
  const breadcrumbStepNumber =
    parentContext?.globalStepNumber != null
      ? parentContext.globalStepNumber
      : typeof parentContext?.stepIndex === "number"
        ? parentContext.stepIndex + 1
        : "";
  const attachmentList = node.detail?.attachments || [];

  
  // Get revisionId and authUser for ping API
  const location = useLocation();
  const params = useParams();
  
  // Extract revisionId from params (could be revisionId or revision_id depending on route)
  const revisionId = params.revisionId || params.revision_id;
  
  // Check if we're on a view-revision route
  const isViewRevisionRoute = location.pathname.includes('/view-revision') || 
                               location.pathname.includes('/view-section') ||
                               location.pathname.includes('/view-step') ||
                               location.pathname.includes('/view-substep') ||
                               location.pathname.includes('/view-childnode');
  
  // Start ping interval if on view-revision route
  useEffect(() => {
    if (!isViewRevisionRoute || !revisionId || !authUser) return;
    
    const interval = setInterval(async () => {
      try {
        const pingData = {
          task_id: revisionId,
          user_id: authUser.id
        };
        await pingTask(pingData);
      } catch (error) {
        console.error('Ping failed in AttachmentNode:', error);
      }
    }, 60000); // 60 seconds = 1 minute
    
    return () => {
      clearInterval(interval);
    };
  }, [isViewRevisionRoute, revisionId, authUser]);

  // Determine checkbox state for this node
  const isCurrent = checkboxState?.currentNodeId === node.tree_node_id &&
    (checkboxState?.currentNodeType === 'attachment' ||
      checkboxState?.currentNodeType === nodeType);
  const isSourceAttachment = checkboxState?.sourceNodeId === node.tree_node_id ||
    checkboxState?.sourceNodeId === (node.detail?.attachment_tree_node_id || node.tree_node_id);
  const isChecked = isCurrent || checkboxState?.allChildrenChecked || isSourceAttachment;
  const isDisabled = disabled || (checkboxState?.allChildrenDisabled && !isCurrent) || (isRevisionLocked && isRevisionLocked.lock && isRevisionLocked.lock.user_id !== authUser?.id);
  const isDescendantLocked = isDescendantLockedPreview(
    checkboxState,
    isCurrent,
    isChecked,
    isSourceAttachment,
  );
  const isAuditPreviewFocusedLeaf = isAuditPreviewFocusedLeafCheckbox(
    checkboxState,
    isCurrent,
    isChecked,
    isSourceAttachment,
  );
  const isPreviewLockedLeafStyle = isDescendantLocked || isAuditPreviewFocusedLeaf;

  if (attachmentList.length === 0) return null;

  return (
    <div
      key={node.tree_node_id}
      data-export-block="attachment"
      style={{ marginTop: showCheckbox ? "20px" : "30px" }}
    >
      {/* Show heading - use showHeading if provided, otherwise fall back to showCheckbox */}
      {(showHeading !== null ? showHeading : showCheckbox) && (
        <div
          style={{
            clear: "both",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: "6px",
              alignItems: "center",
              marginTop: "20px",
            }}
          >
            {showCheckbox && (
              <div
                style={{
                  position: "relative",
                  display: "inline-block",
                  ...auditPreviewSectionDescendantWrapStyle(checkboxState),
                }}
              >
                {isDescendantLocked ? (
                  <input
                    type="checkbox"
                    checked={isChecked}
                    disabled
                    readOnly
                    tabIndex={-1}
                    aria-readonly="true"
                    onChange={() => {}}
                    style={getDescendantLockedCheckboxStyle()}
                  />
                ) : isAuditPreviewFocusedLeaf ? (
                  <div style={getHaloWrapperStyle()}>
                    <input
                      type="checkbox"
                      checked={isChecked}
                      tabIndex={-1}
                      aria-readonly="true"
                      onChange={() => {}}
                      style={getAuditPreviewReadonlyHaloCheckboxStyle()}
                    />
                  </div>
                ) : (
                <input
                  type="checkbox"
                  checked={isChecked}
                  disabled={isDisabled}
                  onChange={() => {
                    // Prevent action for source attachment being moved
                    if (isSourceAttachment) {
                      return;
                    }
                    if (!isDisabled && onNavigate) {
                      onNavigate(nodeType || "attachment", node, { ...parentContext, nodeNumber });
                    }
                  }}
                  style={{
                    cursor: isDisabled ? "not-allowed" : (isSourceAttachment ? "not-allowed" : "pointer"),
                    accentColor: isSourceAttachment
                      ? "rgb(68, 68, 68)"
                      : isChecked
                        ? NODE_SELECTION_BLUE
                        : undefined,
                    backgroundColor: isSourceAttachment ? "rgb(68, 68, 68)" : undefined,
                  }}
                />
                )}
                {/* Red cross icon overlay - shown when disabled and hovering */}
                {isDisabled && !isPreviewLockedLeafStyle && (
                  <div
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      opacity: 0,
                      transition: "opacity 0.2s ease-in-out",
                      pointerEvents: "none",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "100%",
                      height: "100%",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.opacity = "1";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.opacity = "0";
                    }}
                    className="checkbox-hover-overlay"
                  >
                    <FontAwesomeIcon
                      icon={faTimes}
                      size="sm"
                      color="red"
                      style={{
                        filter: "drop-shadow(0 0 1px rgba(255,255,255,0.8))",
                      }}
                    />
                  </div>
                )}
              </div>
            )}
            <h2
              onClick={() => {
                // Prevent action for source attachment being moved
                if (isSourceAttachment) {
                  return;
                }
                if (isPreviewLockedLeafStyle) return;
                if (!isDisabled && showCheckbox && onNavigate) {
                  onNavigate(nodeType || "attachment", node, { ...parentContext, nodeNumber });
                }
              }}
              style={{
                fontSize: "20px",
                margin: 0,
                fontWeight: isPreviewLockedLeafStyle ? 700 : undefined,
                cursor: isSourceAttachment ? "not-allowed" : isPreviewLockedLeafStyle ? "default" : ((!isDisabled && showCheckbox) ? "pointer" : "default"),
                color: isPreviewLockedLeafStyle ? "#111" : showCheckbox ? (checkboxLabelColor || undefined) : undefined,
                userSelect: "none"
              }}
            >{nodeHeading}</h2>
          </div>

          <div
            style={{
              color: "#888",
              fontSize: "14px",
              fontWeight: "normal",
            }}
          >
            {!node.is_deleted &&
              (parentContext?.auditSubstepBreadcrumbFull ? (
                <>{parentContext.auditSubstepBreadcrumbFull}</>
              ) : (
                <>
                  Section {parentContext.sectionIndex + 1} &gt; Step{" "}
                  {breadcrumbStepNumber}
                  {parentContext.substepLetter && (
                    <> &gt; Substep {breadcrumbStepNumber}{parentContext.substepLetter}</>
                  )}
                </>
              ))}
          </div>
        </div>
      )}
      {(() => {
                      const rawShort = node.detail?.short_description || "";
                      // Convert plain text with quotes to HTML format for proper rendering
                      const contentHtml =
                        typeof rawShort === "string" && rawShort.includes('class="editor-shell"')
                          ? rawShort.replace(/^<div class="editor-shell">(.*)<\/div>\s*$/s, "$1")
                          : rawShort && rawShort.trim() !== ""
                            ? rawShort.includes('<') && rawShort.includes('>')
                              ? rawShort // Already HTML, use as-is
                              : `<p>${rawShort.replace(/"/g, '&quot;')}</p>` // Plain text, wrap in p tags
                            : rawShort;
                      return contentHtml ? (
                        <div style={{ marginTop: "5px" }}>
                          <div className="rich-text-content-display">
                            <Suspense fallback={<div>Loading...</div>}>
                              <LexicalComposer
                                initialConfig={{
                                  editorState: null,
                                  html: { import: buildImportMap() },
                                  namespace: "ShortDescriptionViewer",
                                  nodes: [...PlaygroundNodes],
                                  onError: (error) => {
                                    console.error("Error in Short Description viewer:", error);
                                  },
                                  theme: PlaygroundEditorTheme,
                                  readOnly: true,
                                  editable: false,
                                }}
                              >
                                <SharedHistoryContext>
                                  <TableContext>
                                    <ToolbarContext>
                                      <div className="editor-shell">
                                        <Editor
                                          handleEditorChange={() => {}}
                                          initialContent={contentHtml}
                                        />
                                      </div>
                                    </ToolbarContext>
                                  </TableContext>
                                </SharedHistoryContext>
                              </LexicalComposer>
                            </Suspense>
                          </div>
                        </div>
                      ) : null;
                    })()}
      {attachmentList.map((attachment, i) => (
        <div key={i} style={{ marginLeft: "20px" }}>
          {renderPreview(attachment)}
        </div>
      ))}
    </div>
  );
};

export default AttachmentNode;

